import React from 'react';

const ConnectionProcedureViewer = ({ procedure }) => {
  // Si pas de procédure ou procédure vide
  if (!procedure || procedure.trim() === '') {
    return (
      <div className="text-gray-500 italic">
        Aucune procédure de connexion définie
      </div>
    );
  }

  // Essayer de parser en tant que JSON structuré
  let parsedProcedure;
  try {
    parsedProcedure = JSON.parse(procedure);
    if (!parsedProcedure.steps || !Array.isArray(parsedProcedure.steps)) {
      // Pas de structure valide, traiter comme texte simple
      throw new Error('Invalid structure');
    }
  } catch (e) {
    // Traiter comme du texte simple
    return (
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold mb-3 text-gray-800">📋 Procédure de connexion</h3>
        <div className="whitespace-pre-wrap text-gray-700">
          {procedure}
        </div>
      </div>
    );
  }

  // Rendu d'une étape
  const renderStep = (step, index) => {
    const getStepStyle = (type) => {
      switch (type) {
        case 'warning': return 'border-l-4 border-amber-500 bg-amber-50 p-4';
        case 'info': return 'border-l-4 border-blue-500 bg-blue-50 p-4';
        case 'code': return 'bg-gray-900 text-gray-100 p-4 rounded font-mono text-sm';
        default: return 'p-4 bg-white border border-gray-100 rounded';
      }
    };

    return (
      <div key={step.id || index} className="mb-6">
        <h4 className="text-lg font-semibold mb-3 flex items-center">
          <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-3">
            {index + 1}
          </span>
          {step.title || `Étape ${index + 1}`}
        </h4>
        
        <div className={getStepStyle(step.type)}>
          {step.type === 'image' && step.imageUrl && (
            <div className="mb-4">
              <div className="mb-2 text-xs text-gray-600">
                Debug Viewer: Image URL length: {step.imageUrl.length} caractères
                Type: {step.imageUrl.startsWith('data:image/') ? 'Base64' : 'URL'}
              </div>
              <img
                src={step.imageUrl}
                alt={step.imageName || 'Capture d\'écran'}
                className="max-w-full h-auto border border-gray-200 rounded shadow-sm cursor-pointer"
                onError={(e) => {
                  console.error('Erreur chargement image viewer:', e);
                  console.log('URL viewer:', step.imageUrl.substring(0, 100) + '...');
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
                onLoad={() => {
                  console.log('Image viewer chargée avec succès');
                }}
                onClick={() => {
                  // Ouvrir l'image en grand dans une nouvelle fenêtre
                  window.open(step.imageUrl, '_blank');
                }}
                title="Cliquer pour agrandir"
              />
              <div className="text-red-500 text-sm hidden">Erreur de chargement de l'image</div>
              {step.imageName && (
                <p className="text-sm text-gray-500 mt-2 italic">{step.imageName}</p>
              )}
            </div>
          )}
          
          {step.content && (
            <div className={step.type === 'code' ? '' : 'whitespace-pre-wrap'}>
              {step.content}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-50 rounded-lg p-6">
      <h3 className="text-xl font-bold mb-6 text-gray-800 flex items-center">
        📋 Procédure de connexion
        {parsedProcedure.steps && parsedProcedure.steps.length > 0 && (
          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
            {parsedProcedure.steps.length} étape{parsedProcedure.steps.length > 1 ? 's' : ''}
          </span>
        )}
      </h3>
      
      {parsedProcedure.steps && parsedProcedure.steps.length > 0 ? (
        <div className="space-y-6">
          {parsedProcedure.steps.map((step, index) => renderStep(step, index))}
        </div>
      ) : (
        <p className="text-gray-500 italic">Aucune étape définie</p>
      )}
      
      {parsedProcedure.notes && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <h4 className="font-semibold mb-3 text-gray-800">📝 Notes générales</h4>
          <div className="bg-white p-4 rounded-md border">
            <p className="whitespace-pre-wrap text-gray-700">{parsedProcedure.notes}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectionProcedureViewer;
